/**
 * TypebotBubble.tsx — Burbuja de chat de soporte (Typebot self-hosted)
 *
 * Muestra un botón de chat flotante 💬 en la esquina, pero SOLO para
 * administradores. Un usuario es administrador si su `clientData` guardado
 * en localStorage tiene un `token_cliente` no vacío (mismo criterio que usan
 * AppNavbar y BottomNav para decidir el menú de administrador).
 *
 * El bot es self-hosted:
 *   - Viewer (apiHost): https://sisnetel2-typebot-viewer.u5r75b.easypanel.host
 *   - Public ID:        sisnetelweb
 *
 * NOTA: el Public ID y la URL del viewer NO son secretos (son públicos por
 * diseño; van en la URL del bot), por eso pueden vivir en el frontend.
 */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const TYPEBOT_API_HOST = 'https://sisnetel2-typebot-viewer.u5r75b.easypanel.host';
const TYPEBOT_ID = 'sisnetelweb';
// Librería oficial de embed (última 0.x). El navegador la carga como módulo,
// así webpack/CRA no la empaqueta y no rompe el build.
const TYPEBOT_LIB = 'https://cdn.jsdelivr.net/npm/@typebot.io/js@0/dist/web.js';

const SCRIPT_ID = 'typebot-bubble-loader';
const ESTILO_ID = 'sisnetel-typebot-pos';

// Alto de la barra inferior (.bn-bar) + respiro, para que la burbuja quede
// justo ENCIMA de la barra de navegación y no la tape.
const ALTO_BARRA = 70;

// Icono animado de robot (SVG inline, sin dependencias). Animaciones SMIL:
// los ojos parpadean, la antena late y el robot flota suavemente.
const ICONO_BOT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="34" height="34">
  <g fill="none">
    <animateTransform attributeName="transform" type="translate"
      values="0 0; 0 -1.8; 0 0" dur="2.6s" repeatCount="indefinite" calcMode="spline"
      keySplines="0.4 0 0.6 1; 0.4 0 0.6 1" keyTimes="0; 0.5; 1"/>
    <!-- antena -->
    <line x1="32" y1="12" x2="32" y2="19" stroke="#fff" stroke-width="2.6" stroke-linecap="round"/>
    <circle cx="32" cy="9.5" r="3.6" fill="#fff">
      <animate attributeName="r" values="3.6; 4.4; 3.6" dur="1.5s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="1; 0.55; 1" dur="1.5s" repeatCount="indefinite"/>
    </circle>
    <!-- cabeza -->
    <rect x="12" y="19" width="40" height="31" rx="10" fill="#fff"/>
    <!-- orejas -->
    <rect x="7" y="29" width="4.5" height="11" rx="2.2" fill="#fff" opacity="0.85"/>
    <rect x="52.5" y="29" width="4.5" height="11" rx="2.2" fill="#fff" opacity="0.85"/>
    <!-- ojos (parpadeo) -->
    <g fill="#a41619">
      <rect x="21" y="29" width="7" height="9" rx="3.5">
        <animate attributeName="height" values="9;1.2;9" dur="4s" repeatCount="indefinite"
          keyTimes="0;0.06;0.12" calcMode="linear"/>
        <animate attributeName="y" values="29;33;29" dur="4s" repeatCount="indefinite"
          keyTimes="0;0.06;0.12" calcMode="linear"/>
      </rect>
      <rect x="36" y="29" width="7" height="9" rx="3.5">
        <animate attributeName="height" values="9;1.2;9" dur="4s" repeatCount="indefinite"
          keyTimes="0;0.06;0.12" calcMode="linear"/>
        <animate attributeName="y" values="29;33;29" dur="4s" repeatCount="indefinite"
          keyTimes="0;0.06;0.12" calcMode="linear"/>
      </rect>
    </g>
    <!-- boca -->
    <rect x="25" y="42.5" width="14" height="2.8" rx="1.4" fill="#a41619" opacity="0.55"/>
  </g>
</svg>`.trim();

// El icono se pasa a Typebot como data URI (customIconSrc admite una URL).
const ICONO_BOT_DATA_URI =
  'data:image/svg+xml;utf8,' + encodeURIComponent(ICONO_BOT);

// Rutas donde se permite la burbuja. Coincidencia EXACTA: en otras pantallas
// (p. ej. /menu/promesa o /menu/wifi) no debe aparecer.
const RUTAS_PERMITIDAS = ['/menu', '/menu/clientes'];

// ¿El usuario logueado es administrador?
function esAdministrador(): boolean {
  try {
    const raw = localStorage.getItem('clientData');
    if (!raw) return false;
    const d = JSON.parse(raw);
    return Boolean(d?.token_cliente?.toString().trim());
  } catch {
    return false;
  }
}

// Opciones del botón flotante: rojo de marca + icono animado de robot.
// El color va como token del tema: las variables CSS del :root se heredan
// dentro del shadow DOM de Typebot, así que var() se resuelve sin problema.
const OPCIONES_BURBUJA = {
  typebot: TYPEBOT_ID,
  apiHost: TYPEBOT_API_HOST,
  theme: {
    button: {
      backgroundColor: 'var(--sn-accion)',
      customIconSrc: ICONO_BOT_DATA_URI,
    },
  },
};

/**
 * Sube la burbuja por encima de la barra inferior.
 * El componente vive en un shadow DOM cuya clase Tailwind `bottom-(--container-bottom)`
 * fija 20px; las variables CSS del host no la sobreescriben, así que inyectamos
 * un <style> dentro del propio shadow root.
 */
function ajustarPosicion(intentos = 20) {
  const el = document.querySelector('typebot-bubble') as HTMLElement | null;
  if (!el?.shadowRoot) {
    // El custom element aún no montó su shadow root: reintentar en el próximo frame.
    if (intentos > 0) requestAnimationFrame(() => ajustarPosicion(intentos - 1));
    return;
  }
  if (el.shadowRoot.getElementById(ESTILO_ID)) return;
  const style = document.createElement('style');
  style.id = ESTILO_ID;
  style.textContent = `
    /* 1) Subir la burbuja por encima de la barra de navegación inferior, y
       encoger la ventana de chat: por defecto mide 400x553, que en un móvil
       tapa casi toda la pantalla. Las medidas son responsivas para que nunca
       se salga del viewport ni choque con la barra. */
    .fixed.bottom-\\(--container-bottom\\) {
      bottom: calc(${ALTO_BARRA}px + env(safe-area-inset-bottom, 0px) + 14px) !important;
      --bot-max-width: min(224px, calc(100vw - 24px)) !important;
      --bot-max-height: min(500px, calc(100dvh - ${ALTO_BARRA + 105}px)) !important;
    }

    /* 2) Compactar el contenido: Typebot usa 16px y botones de 64px de alto,
       desproporcionado dentro de la app. Esto SOLO afecta al bot embebido
       aquí, no a su versión pública ni a WhatsApp. */
    .typebot-host-bubble,
    .typebot-guest-bubble,
    .typebot-button,
    .typebot-input,
    .typebot-chat-view p,
    .typebot-chat-view span {
      font-size: 11px !important;
      line-height: 1.35 !important;
    }
    .typebot-button {
      padding: 5px 8px !important;
      min-height: 0 !important;
    }
    .typebot-host-bubble > div {
      padding: 6px 9px !important;
    }
  `;
  el.shadowRoot.appendChild(style);
}

// Inicializa la burbuja usando el objeto global si la librería ya se cargó.
function initBubbleSiListo(): boolean {
  const T = (window as any).Typebot;
  if (T && typeof T.initBubble === 'function') {
    T.initBubble(OPCIONES_BURBUJA);
    ajustarPosicion();
    return true;
  }
  return false;
}

const TypebotBubble: React.FC = () => {
  const location = useLocation();

  // Se calcula en cada render para que el efecto reaccione también cuando
  // cambia la sesión (login/logout) y no solo cuando cambia la ruta.
  const admin = esAdministrador();

  useEffect(() => {
    // Se muestra SOLO si es administrador Y está en una de las rutas permitidas.
    const mostrar = admin && RUTAS_PERMITIDAS.includes(location.pathname);
    const bubbleEl = document.querySelector('typebot-bubble');

    // Cliente, logout, o pantalla no permitida → quitar la burbuja.
    if (!mostrar) {
      if (bubbleEl) bubbleEl.remove();
      return;
    }

    // Admin y la burbuja ya está en el DOM → solo reasegurar la posición.
    if (bubbleEl) { ajustarPosicion(); return; }

    // Admin y la librería ya cargó antes → re-inicializar sin recargar script.
    if (initBubbleSiListo()) return;

    // Primera vez: inyectar el script de módulo con el snippet oficial.
    if (document.getElementById(SCRIPT_ID)) return;
    const script = document.createElement('script');
    script.type = 'module';
    script.id = SCRIPT_ID;
    script.innerHTML = `
      import Typebot from '${TYPEBOT_LIB}'
      window.Typebot = Typebot
      Typebot.initBubble(${JSON.stringify(OPCIONES_BURBUJA)})
      window.dispatchEvent(new Event('typebot-listo'))
    `;
    window.addEventListener('typebot-listo', () => ajustarPosicion(), { once: true });
    document.body.appendChild(script);
  }, [location.pathname, admin]);

  return null;
};

export default TypebotBubble;
